import { HeaderContext } from '../context'
import React, { useCallback } from 'react'

interface UseProviderStickyBarHeightHook {
  stickyBarHeight: number | undefined
  setStickyBarHeight: (value: number) => void
}

/**
 * Hook to get and set the sticky bar height in the header context.
 * Use this to track the height of sticky content bars (like date bars) that appear below the main header.
 *
 * @returns Object with stickyBarHeight (in pixels) and setStickyBarHeight function
 *
 * @example
 * const { stickyBarHeight, setStickyBarHeight } = useStickyBarHeight()
 * // Use stickyBarHeight for calculating tour spotlight padding or other layout adjustments
 */
export const useStickyBarHeight = (): UseProviderStickyBarHeightHook => {
  const contextValue = React.useContext(HeaderContext)

  if (contextValue === undefined) {
    throw new Error('useStickyBarHeight must be used within HeaderProvider')
  }

  const setStickyBarHeight = useCallback(
    (value: number): void => {
      contextValue?.setStickyBarHeight(value)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextValue.setStickyBarHeight]
  )

  return { stickyBarHeight: contextValue?.stickyBarHeight, setStickyBarHeight }
}
